import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMode } from '@/providers/ModeProvider';

/**
 * Bridge component that syncs mode changes to React Query cache
 * 
 * This component sits inside QueryProvider but under ModeProvider,
 * allowing it to access both contexts. When the mode changes,
 * it cancels in-flight queries and clears the cache to ensure 
 * fresh data is fetched for the new mode.
 */
export function ModeQuerySync({ children }: { children: React.ReactNode }) {
  const { mode } = useMode();
  const queryClient = useQueryClient();
  const prevModeRef = useRef(mode);

  useEffect(() => {
    // Only clear cache when mode actually changes, not on initial mount
    if (prevModeRef.current !== mode) {
      // Cancel all in-flight queries to abort their network requests
      queryClient.cancelQueries();
      
      // Clear the cache to force fresh fetches for the new mode
      queryClient.clear();
      
      prevModeRef.current = mode;
    }
  }, [mode, queryClient]);

  return <>{children}</>;
}
